/* eslint-disable consistent-return */
// TODO: Improve formatting so it works for any currency, currently fails for Yen or HUF
import {
	number, object, string, array,
} from 'yup';
import {
	CreateCampaignObjective,
	CreateGroupBidStrategy,
	CreateGroupGoal,
	GroupOptimizationGoal,
	GroupTargetingDevicesOs,
	GroupTargetingLocations,
	PixelEventsDocument,
} from 'wb-reddit/graphql/generated';
import { nameValidator } from 'wb-reddit/app/routes/superGrid/validation/utils';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import Dinero, { Currency } from 'dinero.js';
import dayjs from 'dayjs';
import { toInteger } from 'lodash';
import { getApolloClient } from 'auth/lib/util';

function formatCurrency(amount: number, currency: Currency) {
	return Dinero({ amount: toInteger((amount ?? 0) * 100), currency }).toFormat('$0.00');
}

const getGroupBidAmountConstraintForType = (objective: CreateCampaignObjective) => {
	switch (objective) {
		case CreateCampaignObjective.Impressions: // BRAND AWARENESS AND REACH
			return { min: 3.5, max: 100 };
		case CreateCampaignObjective.VideoViewableImpressions: // VIEW VIEWS
			return { min: 0, max: 1 };
	}

	return { min: 0.1, max: 100 };
};

const placementValidationSchema = array(object({
	value: string().required("Placement's value is required"),
})).notRequired();
const getPlacementValidationSchema = (data: Record<string, any>) => (
	data.parent.campaignObjective === CreateCampaignObjective.VideoViewableImpressions
		? placementValidationSchema.test({
			name:    'doesnt-contain-comments-page',
			test:    (value) => !value?.find(item => item.value === GroupTargetingLocations.CommentsPage),
			message: "Can't use Conversations placement with video views objective",
		}) : placementValidationSchema
);
const customAudiencesValidationSchema = array(object({
	value: string().required("Custom audience's value is required"),
})).notRequired();
const communitiesValidationSchema = array(object({
	value: string().required("Community's value is required"),
})).notRequired();
const locationsValidationSchema = array(object({
	value: string().required("Location's value is required"),
})).notRequired();
const keywordsValidationSchema = (data) => array(string()).test({
	name: 'not-combined',
	test: (value) => (
		!data?.communities?.length
		&& !data?.communitiesExcluded?.length
		&& !data?.interests?.length
	) || !value?.length,
	message: 'Keyword targeting cannot be combined with Community or Interest targetings.',
}).test({
	name: 'regex-test',
	test: value => {
		const conditionRegex = /^([a-zA-Z0-9'. ]*)$/;

		return value?.every(item => conditionRegex.test(item ?? ''));
	},
	message: 'Only alphanumeric characters, spaces, dots and apostrophes are allowed.',
}).notRequired();

const duplicatesValidationSchema = (data, fieldIncluded: string, fieldExcluded: string) => {
	const includedValues = data[fieldIncluded]?.map(item => item.value);
	const excludedValues = data[fieldExcluded]?.map(item => item.value);

	const duplicatedValues = includedValues?.filter(value => excludedValues?.includes(value));
	const duplicatedValueLabels = duplicatedValues?.map(
		value => data[fieldIncluded]?.find(item => item.value === value)?.label,
	);

	return array(object({
		value: string().required('Value is required'),
	})).test({
		name:    'no-duplicates',
		test:    () => !duplicatedValues?.length,
		message: `${duplicatedValueLabels?.join(', ')} cannot be in both included and excluded lists.`,
	}).notRequired();
};

export const getRedditGroupSchema = (data: Record<string, any>) => {
	/* const createValidations = { TODO: Add this back if we ever expose groupBidType as a column
		groupBidType: string().required('Group bid type is required')
			.oneOf(
				[getGroupBidTypeForObjective(data?.parent?.campaignObjective)],
				"Incorrect Group bid type for the campaign's objective",
			),
	}; */

	const amountConstraint = getGroupBidAmountConstraintForType(data?.parent?.campaignObjective);

	return object().shape({
		groupName:        nameValidator,
		groupBidStrategy: string().required('Group bid strategy is required')
			.oneOf(Object.values(CreateGroupBidStrategy)),
		groupBidAmount: number().required('Bid amount is required')
			.min(
				amountConstraint.min * divideCurrencyFields.bid,
				`Bid amount has to be at least ${formatCurrency(amountConstraint.min, data.currency)}`,
			)
			.lessThan(
				amountConstraint.max * divideCurrencyFields.bid,
				`Bid amount must be lower than ${formatCurrency(amountConstraint.max, data.currency)}`,
			),
		groupOptimizationGoal: string().when([], {
			is:        () => data?.parent?.campaignObjective === CreateCampaignObjective.Conversions,
			then:      (schema) => schema.required('Conversion goal is required'),
			otherwise: (schema) => schema.notRequired(),
		}).test(
			'was-goal-fired',
			'This goal is not set-up or reported yet for this account',
			async (value) => {
				if (!data?.isInCreation || !value) {
					return true;
				}

				const fetchedPixelEvents = ((await getApolloClient()?.query({
					query:       PixelEventsDocument,
					errorPolicy: 'ignore',
					variables:   { adAccountId: data.parent?.parent?.adAccount?.adAccountId ?? '' },
				}))?.data?.pixelLastFiredAt ?? []).filter(e => !!e);

				if (!fetchedPixelEvents || fetchedPixelEvents?.length === 0) {
					return [GroupOptimizationGoal.Clicks].includes(value as GroupOptimizationGoal);
				}

				return fetchedPixelEvents.includes(value as GroupOptimizationGoal);
			},
		),
		groupGoal:      string().required('Budget type is required').oneOf(Object.values(CreateGroupGoal)),
		groupStartTime: string().required('Start time is required'),
		groupEndTime:   string().when('groupGoal', {
			is: CreateGroupGoal.LifetimeSpend,
			then(schema) {
				return schema.required('End time is required when group goal is lifetime spend');
			},
			otherwise(schema) {
				return schema.notRequired();
			},
		}).test({
			name:    'after-start-time',
			test:    (value) => !value || dayjs(value).isAfter(dayjs(data.groupStartTime)),
			message: 'End time has to be after start time',
		}).test({
			name:    'after-start-time',
			test:    (value) => !value || dayjs(value).isAfter(dayjs(data.groupStartTime).add(1, 'hour')),
			message: 'Difference between end and start time needs to be at least 1 hour',
		}),
		groupGoalAmount: number().notRequired().moreThan(
			data.groupBidAmount,
			'Budget has to be greater than bid amount',
		).test({
			name: 'is-days-amount',
			test: value => data?.groupGoal === CreateGroupGoal.DailySpend
				|| dayjs(data.groupEndTime).diff(dayjs(data.groupStartTime), 'days') * 5
				<= (Number(value) / divideCurrencyFields.budget),
			message: `Total budget has to be for at least ${formatCurrency(5, data.currency)} for each day (${formatCurrency(dayjs(data.groupEndTime).diff(dayjs(data.groupStartTime), 'days') * 5, data.currency)})`,
		}).test({
			name: 'is-atleast-5',
			test: value => data?.groupGoal === CreateGroupGoal.LifetimeSpend
				|| 5 * divideCurrencyFields.budget <= Number(value),
			message: `Gudget has to be at least ${formatCurrency(5, data.currency)} when daily spend is selected`,
		}),
		groupAppId: string().when([], {
			is: () => data?.parent?.campaignObjective === CreateCampaignObjective.AppInstalls,
			then(schema) {
				return schema.required('App URL / ID is required');
			},
			otherwise(schema) {
				return schema.notRequired();
			},
		}),

		customAudiences:         duplicatesValidationSchema(data, 'customAudiences', 'customAudiencesExcluded'),
		customAudiencesExcluded: customAudiencesValidationSchema,

		placements:         getPlacementValidationSchema(data),
		placementsExcluded: placementValidationSchema,

		communities:         duplicatesValidationSchema(data, 'communities', 'communitiesExcluded'),
		communitiesExcluded: communitiesValidationSchema,

		locations:         duplicatesValidationSchema(data, 'locations', 'locationsExcluded'),
		locationsExcluded: locationsValidationSchema,

		keywords:   keywordsValidationSchema(data),
		deviceType: array(object({
			value: string().required('Device type is required')
				.oneOf([...Object.values(GroupTargetingDevicesOs), 'DESKTOP'], 'Incorrect device type'),
		})).when([], {
			is: () => data?.parent?.campaignObjective === CreateCampaignObjective.AppInstalls,
			then(schema) {
				return schema.required('Device type is required').length(1, 'Select exactly one device type');
			},
			otherwise(schema) {
				return schema.notRequired();
			},
		}),
		// ...(data.isInCreation ? createValidations : {}),
	});
};

export const validateRedditGroup = (data: Record<string, any>) => getRedditGroupSchema(data).validate(data, { abortEarly: false });
